import { useState, useMemo, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
    ShieldAlert,
    LayoutDashboard,
    Link2,
    Users,
    Activity,
    Trash2,
    ShieldCheck,
    Plus,
    X,
    MessageSquare,
    Eye,
    Check,
    Key,
    Copy,
    Bot,
    ToggleRight,
    Settings,
    FileText,
    Zap,
    Loader2,
    RefreshCw
} from 'lucide-react'
import { useAdmin } from '../contexts/AdminContext'
import { useAuth } from '../contexts/AuthContext'
import { RELAYS as STATIC_RELAYS } from '../config/constants'
import { useNostrTopics } from '../hooks/useNostrTopics'
import { delegationService } from '../services/delegationService'
import { reportService } from '../services/reportService'
import * as botService from '../services/botService'
import { useNostrProfile } from '../hooks/useNostrProfile'
import { useGlobalModeration } from '../hooks/useGlobalModeration'
import { getNostrPool } from '../lib/nostrPool'
import { Event } from 'nostr-tools'
import { bytesToHex } from '@noble/hashes/utils'


type Tab = 'overview' | 'moderation' | 'relays' | 'users' | 'delegation' | 'reports' | 'entries' | 'moderation_log' | 'bots' | 'features' | 'system'

export function AdminDashboard() {
    const [activeTab, setActiveTab] = useState<Tab>('overview')
    const { settings, updateSettings, muteUser, unmuteUser } = useAdmin()
    const { pubkey } = useAuth()
    const { metadata } = useNostrProfile(pubkey)
    const { topics } = useNostrTopicStats() // Custom hook defined below for stats

    const displayName = metadata?.display_name || metadata?.name || (pubkey ? `${pubkey.slice(0, 12)}...` : '')
    const avatarUrl = metadata?.picture || null

    const allRelays = useMemo(() => {
        return [...new Set([...STATIC_RELAYS, ...settings.customRelays])]
    }, [settings.customRelays])

    const sidebarItems = [
        { id: 'overview', label: 'Genel Bakış', icon: LayoutDashboard },
        { id: 'features', label: 'Özellik Kontrolü', icon: ToggleRight },
        { id: 'system', label: 'Sistem Ayarları', icon: Settings },
        { id: 'entries', label: 'Tüm Entryler', icon: MessageSquare },
        { id: 'reports', label: 'Bildirimler', icon: ShieldAlert },
        { id: 'users', label: 'Kullanıcılar', icon: Users },
        { id: 'moderation_log', label: 'Moderasyon Kaydı', icon: ShieldCheck },
        { id: 'moderation', label: 'Filtreler', icon: ShieldCheck },
        { id: 'relays', label: 'Sunucu Yönetimi', icon: Activity },
        { id: 'delegation', label: 'Yetki Paylaşımı', icon: Key },
        { id: 'bots', label: 'Bot Yönetimi', icon: Bot },
    ]

    return (
        <div className="min-h-[80vh] flex flex-col md:flex-row gap-6">
            {/* Dashboard Sidebar */}
            <aside className="w-full md:w-64 space-y-2">
                <div className="p-4 bg-mavi-600 rounded-xl text-white mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
                            {avatarUrl ? (
                                <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
                            ) : (
                                <ShieldCheck className="w-6 h-6" />
                            )}
                        </div>
                        <div className="min-w-0">
                            <h2 className="font-poppins font-bold text-sm leading-tight truncate" title={displayName}>{displayName}</h2>
                            <p className="text-[10px] opacity-70 font-mono uppercase tracking-widest mt-1">Admin Yetkisi</p>
                        </div>
                    </div>
                </div>

                <nav className="space-y-1">
                    {sidebarItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id as Tab)}
                            className={`w-full flex items-center gap-3 p-3 rounded-xl font-poppins font-medium transition-all ${activeTab === item.id
                                ? 'bg-mavi-500 text-white shadow-lg shadow-mavi-500/20'
                                : 'text-gray-500 dark:text-gray-400 hover:bg-surface-100 dark:hover:bg-surface-800'
                                }`}
                        >
                            <item.icon className="w-5 h-5" />
                            <span>{item.label}</span>
                        </button>
                    ))}
                </nav>
            </aside>

            {/* Main Content Area */}
            <main className="flex-1 bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-800 shadow-sm overflow-hidden">
                <div className="p-6 h-full flex flex-col">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="h-full"
                        >
                            {activeTab === 'overview' && <OverviewTab allRelays={allRelays} topics={topics} />}
                            {activeTab === 'features' && <FeaturesTab />}
                            {activeTab === 'system' && <SystemTab />}
                            {activeTab === 'reports' && <ReportsTab />}
                            {activeTab === 'entries' && <GlobalEntriesTab />}
                            {activeTab === 'users' && (
                                <UsersTab
                                    mutedUsers={settings.mutedUsers || []}
                                    muteUser={muteUser}
                                    unmuteUser={unmuteUser}
                                />
                            )}
                            {activeTab === 'moderation_log' && <ModerationLogTab />}
                            {activeTab === 'moderation' && <ModerationTab />}
                            {activeTab === 'relays' && (
                                <RelaysTab
                                    allRelays={allRelays}
                                    settings={settings}
                                    updateSettings={updateSettings}
                                />
                            )}
                            {activeTab === 'delegation' && <DelegationTab />}
                            {activeTab === 'bots' && <BotsTab setActiveTab={setActiveTab} />}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </main>
        </div>
    )
}

// --- Sub-components for each tab ---

function OverviewTab({ allRelays, topics }: { allRelays: string[], topics: any[] }) {
    return (
        <div className="space-y-8">
            <header className="flex items-center justify-between">
                <div>
                    <h2 className="font-poppins font-bold text-2xl text-gray-900 dark:text-white">Genel Bakış</h2>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Sistem durumu ve istatistikler.</p>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 bg-green-500/10 text-green-600 rounded-full text-xs font-bold font-poppins animate-pulse">
                    <Activity className="w-4 h-4" />
                    CANLI
                </div>
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <StatCard label="Aktif Relay" value={allRelays.length.toString()} icon={Link2} color="text-mavi-500" />
                <StatCard label="Aktif Başlık" value={topics.length.toString()} icon={MessageSquare} color="text-purple-500" />
                <StatCard label="Namespace" value="#mavililer" icon={ShieldCheck} color="text-kirmizi-500" />
            </div>

            <div className="space-y-4">
                <h3 className="font-poppins font-semibold text-gray-900 dark:text-white">Bağlantı Durumu</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {allRelays.map((url) => (
                        <div key={url} className="flex items-center justify-between p-3 bg-surface-50 dark:bg-surface-800 rounded-lg border border-surface-100 dark:border-surface-700">
                            <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{url}</span>
                            <div className="w-2 h-2 rounded-full bg-green-500 shadow-sm shadow-green-500/50" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

function ModerationTab() {
    const { settings, addBannedWord, removeBannedWord, updateSettings } = useAdmin()
    const [newWord, setNewWord] = useState('')

    const handleAddWord = () => {
        if (!newWord.trim()) return
        addBannedWord(newWord.trim())
        setNewWord('')
    }

    return (
        <div className="space-y-8">
            <header>
                <h2 className="font-poppins font-bold text-2xl text-gray-900 dark:text-white">Moderasyon Paneli</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm">İçerik filtreleme ve moderasyon kuralları.</p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Banned Words Section */}
                <div className="space-y-4">
                    <div className="bg-white dark:bg-surface-900 rounded-2xl p-6 border border-surface-200 dark:border-surface-700 h-full">
                        <h3 className="font-poppins font-semibold text-lg text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <ShieldAlert className="w-5 h-5 text-red-500" />
                            Yasaklı Kelimeler
                        </h3>

                        <div className="flex gap-2 mb-4">
                            <input
                                value={newWord}
                                onChange={(e) => setNewWord(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleAddWord()}
                                placeholder="Kelime ekle..."
                                className="flex-1 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500"
                            />
                            <button
                                onClick={handleAddWord}
                                className="p-2 bg-mavi-500 text-white rounded-xl hover:bg-mavi-600 transition-colors"
                            >
                                <Plus className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {settings.bannedWords.length === 0 ? (
                                <p className="text-sm text-gray-400 italic">Henüz yasaklı kelime yok.</p>
                            ) : (
                                settings.bannedWords.map((word) => (
                                    <div key={word} className="flex items-center gap-2 px-3 py-1.5 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-lg group transition-all hover:bg-red-100 dark:hover:bg-red-900/20">
                                        <span className="text-sm font-medium text-red-700 dark:text-red-400">{word}</span>
                                        <button
                                            onClick={() => removeBannedWord(word)}
                                            className="text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-300"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Deleted Content Stats */}
                <div className="space-y-4">
                    <div className="bg-white dark:bg-surface-900 rounded-2xl p-6 border border-surface-200 dark:border-surface-700 h-full">
                        <h3 className="font-poppins font-semibold text-lg text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <Trash2 className="w-5 h-5 text-gray-500" />
                            Silinen İçerikler (Yerel)
                        </h3>

                        <div className="p-4 bg-surface-50 dark:bg-surface-800 rounded-xl border border-surface-100 dark:border-surface-700 mb-4">
                            <div className="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                                {settings.deletedEventIds.length}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                Yerel olarak gizlenen/silinen entry sayısı.
                            </div>
                        </div>

                        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-900/30 rounded-xl">
                            <p className="text-xs text-yellow-800 dark:text-yellow-500 leading-relaxed">
                                <strong>Bilgi:</strong> Bir entry'yi sildiğinizde hem Nostr relay'lerine "Kind 5" silme talebi gönderilir, hem de bu tarayıcıda anında gizlenir.
                                Bu liste yerel olarak gizlenenleri gösterir.
                            </p>
                        </div>

                        {settings.deletedEventIds.length > 0 && (
                            <button
                                onClick={() => {
                                    if (window.confirm('Gizlenen içerik listesini temizlemek istediğinize emin misiniz? Silinen içerikler relaylerden geri gelebilir.')) {
                                        updateSettings({ deletedEventIds: [] })
                                    }
                                }}
                                className="mt-4 w-full py-2 px-4 border border-red-200 dark:border-red-900/30 text-red-600 dark:text-red-400 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors text-sm font-medium"
                            >
                                Listeyi Temizle (Geri Al)
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

function UsersTab({ mutedUsers, unmuteUser, muteUser }: { mutedUsers: string[], unmuteUser: (pk: string) => void, muteUser: (pk: string) => void }) {
    const [manualPk, setManualPk] = useState('')

    const handleManualMute = () => {
        if (manualPk.length < 10) return
        muteUser(manualPk)
        setManualPk('')
    }

    return (
        <div className="space-y-8">
            <h2 className="font-poppins font-bold text-2xl">Kullanıcı Yönetimi</h2>

            {/* Manual Mute */}
            <div className="p-6 bg-surface-50 dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700 space-y-4">
                <h3 className="text-sm font-poppins font-semibold text-mavi-600 uppercase tracking-wider flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4" />
                    Kullanıcı Engelle (Pubkey)
                </h3>
                <div className="flex gap-2">
                    <input
                        value={manualPk}
                        onChange={(e) => setManualPk(e.target.value)}
                        placeholder="npub veya hex pubkey"
                        className="flex-1 bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-700 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500"
                    />
                    <button
                        onClick={handleManualMute}
                        className="btn-primary"
                    >
                        Engelle
                    </button>
                </div>
            </div>

            <div className="space-y-4">
                <h3 className="text-sm font-poppins font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Sessize Alınanlar ({mutedUsers.length})
                </h3>
                {mutedUsers.length === 0 ? (
                    <p className="text-gray-400 text-sm italic">Henüz engellenmiş bir kullanıcı yok.</p>
                ) : (
                    <div className="space-y-2">
                        {mutedUsers.map((pk) => (
                            <div key={pk} className="flex items-center justify-between p-4 bg-red-500/5 dark:bg-red-500/10 border border-red-500/20 rounded-xl">
                                <span className="font-mono text-xs text-red-600 dark:text-red-400 break-all">{pk}</span>
                                <button
                                    onClick={() => unmuteUser(pk)}
                                    className="p-2 hover:bg-red-500/10 rounded-lg text-red-500 transition-colors"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

function RelaysTab({ allRelays, settings, updateSettings }: { allRelays: string[], settings: any, updateSettings: any }) {
    const [newRelay, setNewRelay] = useState('')

    const addRelay = () => {
        if (!newRelay.startsWith('ws')) return
        updateSettings({ customRelays: [...settings.customRelays, newRelay] })
        setNewRelay('')
    }

    return (
        <div className="space-y-6">
            <h2 className="font-poppins font-bold text-2xl">Relay Yönetimi</h2>

            <div className="flex gap-2">
                <input
                    value={newRelay}
                    onChange={(e) => setNewRelay(e.target.value)}
                    placeholder="wss://yeni-relay.com"
                    className="flex-1 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-mavi-500"
                />
                <button
                    onClick={addRelay}
                    className="p-2 bg-mavi-500 text-white rounded-xl hover:bg-mavi-600 transition-colors"
                >
                    <Plus className="w-5 h-5" />
                </button>
            </div>

            <div className="space-y-2">
                {allRelays.map((url) => (
                    <div key={url} className="flex items-center justify-between p-3 bg-surface-50 dark:bg-surface-800 rounded-lg group">
                        <span className="text-xs font-mono">{url}</span>
                        {settings.customRelays.includes(url) && (
                            <button
                                onClick={() => updateSettings({ customRelays: settings.customRelays.filter((r: string) => r !== url) })}
                                className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                ))}
            </div>
        </div>
    )
}

function StatCard({ label, value, icon: Icon, color }: any) {
    return (
        <div className="p-4 bg-surface-50 dark:bg-surface-800 rounded-2xl border border-surface-100 dark:border-surface-700">
            <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg bg-white dark:bg-surface-900 shadow-sm ${color}`}>
                    <Icon className="w-5 h-5" />
                </div>
                <span className="text-xs font-medium text-gray-400 uppercase tracking-widest">{label}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white pl-1">
                {value}
            </div>
        </div>
    )
}

function useNostrTopicStats() {
    const { topics } = useNostrTopics()
    return { topics }
}

// --- Delegation Management Tab ---

function DelegationTab() {
    const { sk } = useAuth()
    const [newDelegatePubkey, setNewDelegatePubkey] = useState('')
    const [newConditions, setNewConditions] = useState('kind=1')
    const [isCreating, setIsCreating] = useState(false)
    const [generatedToken, setGeneratedToken] = useState('')
    const [error, setError] = useState('')

    const handleCreateDelegation = async () => {
        setError('')
        setGeneratedToken('')

        if (!newDelegatePubkey.trim() || !sk) {
            setError('Lütfen bot pubkey girin ve admin olarak giriş yaptığınızdan emin olun.')
            return
        }

        setIsCreating(true)
        try {
            const days = 30 // Default 30 days for now
            const since = Math.floor(Date.now() / 1000)
            const until = since + (days * 24 * 60 * 60)

            // delegationService expects an object for conditions
            const conditionsObj = {
                kind: 1,
                since,
                until
            }

            const token = delegationService.createDelegation(
                newDelegatePubkey.trim(),
                conditionsObj,
                bytesToHex(sk)
            )

            setGeneratedToken(token)
            setNewDelegatePubkey('')
        } catch (error: any) {
            console.error(error)
            setError(error.message || 'Delegation oluşturulamadı')
        } finally {
            setIsCreating(false)
        }
    }

    return (
        <div className="space-y-8">
            <header>
                <h2 className="font-poppins font-bold text-2xl text-gray-900 dark:text-white mb-2">Bot Yetkileri (NIP-26)</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Botlara ve servislere adınıza içerik yayınlama yetkisi verin.</p>
            </header>

            {/* Create New Delegation */}
            <div className="bg-white dark:bg-surface-900 rounded-2xl p-6 border border-surface-200 dark:border-surface-700 space-y-4">
                <h3 className="font-poppins font-semibold text-lg text-gray-900 dark:text-white flex items-center gap-2">
                    <Plus className="w-5 h-5 text-mavi-500" />
                    Yeni Yetki Oluştur
                </h3>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-poppins font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Bot/Servis Pubkey
                        </label>
                        <input
                            type="text"
                            value={newDelegatePubkey}
                            onChange={(e) => setNewDelegatePubkey(e.target.value)}
                            placeholder="hex pubkey (e.g. 5c26...)"
                            className="w-full p-3 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-mavi-500 font-mono text-sm"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-poppins font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Yetkiler (Conditions)
                        </label>
                        <input
                            type="text"
                            value={newConditions}
                            onChange={(e) => setNewConditions(e.target.value)}
                            placeholder="kind=1 (Şimdilik sadece Kind 1 destegi)"
                            disabled
                            className="w-full p-3 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-mavi-500 font-mono text-sm opacity-60"
                        />
                    </div>

                    {error && (
                        <div className="p-3 bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 text-sm rounded-xl">
                            {error}
                        </div>
                    )}

                    <button
                        onClick={handleCreateDelegation}
                        disabled={isCreating || !newDelegatePubkey.trim()}
                        className="w-full py-3 px-6 bg-mavi-500 hover:bg-mavi-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl font-poppins font-bold transition-all flex items-center justify-center gap-2"
                    >
                        {isCreating ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Oluşturuluyor...
                            </>
                        ) : (
                            <>
                                <ShieldCheck className="w-4 h-4" />
                                Delegation Token Oluştur
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Generated Token Result */}
            {generatedToken && (
                <div className="bg-surface-50 dark:bg-surface-800 rounded-2xl p-6 border border-surface-200 dark:border-surface-700 flex flex-col items-center justify-center text-center">
                    <div className="w-full space-y-4">
                        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-2">
                            <Check className="w-8 h-8 text-green-500" />
                        </div>
                        <h3 className="font-bold text-gray-900 dark:text-white text-lg">Token Hazır!</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                            Bu stringi botunuzun yapılandırmasına ekleyin.
                        </p>

                        <div className="relative group text-left">
                            <textarea
                                readOnly
                                value={generatedToken}
                                className="w-full h-32 p-4 bg-white dark:bg-surface-950 border border-surface-200 dark:border-surface-700 rounded-xl font-mono text-xs resize-none focus:outline-none"
                            />
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(generatedToken)
                                    alert('Kopyalandı!')
                                }}
                                className="absolute top-2 right-2 p-2 bg-surface-100 dark:bg-surface-800 hover:bg-surface-200 rounded-lg transition-colors text-mavi-600"
                            >
                                <Copy className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

function ReportsTab() {
    const { settings, dismissReport, deleteEventLocally, muteUser } = useAdmin()
    const [reports, setReports] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        loadReports()
    }, [])

    const loadReports = async () => {
        setLoading(true)
        try {
            const data = await reportService.fetchReports()
            setReports(data)
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }

    // Filter reports based on dismissed IDs
    const activeReports = reports.filter(r => !settings.dismissedReportIds.includes(r.id))

    const handleDismiss = (id: string) => {
        if (window.confirm('Bu bildirimi gizlemek istediğinize emin misiniz?')) {
            dismissReport(id)
        }
    }

    const handleDelete = async (reportId: string, targetId: string) => {
        if (!targetId) return
        if (window.confirm('Bildirilen içeriği silmek istediğinize emin misiniz? (Kind 5 yayınlanacak)')) {
            try {
                await reportService.deleteReportedContent(targetId)
                deleteEventLocally(targetId) // Hide locally
                dismissReport(reportId) // Also dismiss the report as dealt with
                alert('İçerik silindi ve rapor kapatıldı.')
            } catch (e) {
                console.error(e)
                alert('Silme işlemi başarısız. Yetkiniz olmayabilir.')
            }
        }
    }

    const handleStrongDelete = async (reportId: string, targetId: string) => {
        if (!targetId) return
        if (window.confirm('Bu içeriği "Mavililer" namespace\'inden KALICI olarak gizlemek istediğinize emin misiniz? (Global Moderation List\'e eklenecek)')) {
            try {
                // 1. Send Kind 5 (Standard Delete)
                await reportService.deleteReportedContent(targetId)

                // 2. Add to Global Moderation List (Kind 30000)
                // Dynamically import to avoid circular dependencies if any
                const { addToList } = await import('../services/listService')
                await addToList('moderation', { type: 'event', value: targetId }, null, 'moderation_log')

                // 3. Local Cleanup
                deleteEventLocally(targetId)
                dismissReport(reportId)
                alert('İçerik Kind 5 ile silindi ve Global Moderasyon Listesine eklendi.')
            } catch (e) {
                console.error(e)
                alert('İşlem başarısız.')
            }
        }
    }

    const handleBan = (reportId: string, targetPubkey: string) => {
        if (!targetPubkey) return
        if (window.confirm('Kullanıcıyı engellemek istediğinize emin misiniz?')) {
            muteUser(targetPubkey)
            dismissReport(reportId) // Dismiss report
            alert('Kullanıcı engellendi.')
        }
    }

    return (
        <div className="space-y-8">
            <header>
                <h2 className="font-poppins font-bold text-2xl text-gray-900 dark:text-white mb-2">Bildirimler</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Kullanıcı bildirimleri ve şikayetler.</p>
            </header>

            <div className="bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-700 overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-500">Yükleniyor...</div>
                ) : activeReports.length === 0 ? (
                    <div className="p-8 text-center flex flex-col items-center gap-3">
                        <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center text-green-600 dark:text-green-400">
                            <Check className="w-6 h-6" />
                        </div>
                        <p className="font-medium text-gray-900 dark:text-white">Şikayet Bulunmuyor</p>
                        <p className="text-sm text-gray-500">Temiz!</p>
                    </div>
                ) : (
                    <div className="divide-y divide-surface-100 dark:divide-surface-800">
                        {activeReports.map((report) => {
                            const targetId = report.tags.find((t: string[]) => t[0] === 'e')?.[1]
                            const targetPubkey = report.tags.find((t: string[]) => t[0] === 'p')?.[1]
                            const reason = report.tags.find((t: string[]) => t[0] === 'report')?.[1] || 'Report'

                            return (
                                <div key={report.id} className="p-6 hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors">
                                    <div className="flex flex-col md:flex-row gap-4 justify-between items-start">
                                        <div className="flex-1 space-y-2">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-bold rounded uppercase">
                                                    {reason}
                                                </span>
                                                <span className="text-xs text-gray-500 font-mono" title={report.id}>ID: {report.id.slice(0, 8)}...</span>
                                                <span className="text-xs text-gray-400">oleh: {report.pubkey.slice(0, 8)}...</span>
                                            </div>

                                            <div className="p-3 bg-surface-50 dark:bg-surface-950 rounded-lg border border-surface-100 dark:border-surface-800">
                                                <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                                                    "{report.content || 'Açıklama yok.'}"
                                                </p>
                                            </div>

                                            <div className="flex gap-4 text-xs text-gray-500 font-mono mt-2">
                                                {targetId && <span>Target Event: {targetId.slice(0, 8)}...</span>}
                                                {targetPubkey && <span>Accused User: {targetPubkey.slice(0, 8)}...</span>}
                                            </div>
                                        </div>

                                        <div className="flex flex-row md:flex-col gap-2 shrink-0">
                                            <button
                                                onClick={() => handleDismiss(report.id)}
                                                className="px-3 py-1.5 text-xs font-medium bg-gray-100 dark:bg-surface-800 hover:bg-gray-200 dark:hover:bg-surface-700 rounded-lg transition-colors text-gray-600 dark:text-gray-300"
                                            >
                                                Yoksay (Dismiss)
                                            </button>
                                            {targetId && (
                                                <>
                                                    <button
                                                        onClick={() => handleDelete(report.id, targetId)}
                                                        className="px-3 py-1.5 text-xs font-medium bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-600 dark:text-red-400"
                                                    >
                                                        İçeriği Sil
                                                    </button>
                                                    <button
                                                        onClick={() => handleStrongDelete(report.id, targetId)}
                                                        className="px-3 py-1.5 text-xs font-bold bg-red-600 text-white hover:bg-red-700 rounded-lg transition-colors flex items-center justify-center gap-1"
                                                        title="Mavililer Namespace'inden kalıcı olarak gizle"
                                                    >
                                                        <ShieldAlert className="w-3 h-3" />
                                                        Güçlü Sil (Global)
                                                    </button>
                                                </>
                                            )}
                                            {targetPubkey && (
                                                <button
                                                    onClick={() => handleBan(report.id, targetPubkey)}
                                                    className="px-3 py-1.5 text-xs font-medium bg-orange-50 dark:bg-orange-900/10 hover:bg-orange-100 dark:hover:bg-orange-900/20 rounded-lg transition-colors text-orange-600 dark:text-orange-400"
                                                >
                                                    Kullanıcıyı Engelle
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}

function GlobalEntriesTab() {
    return <GlobalEntriesTabContent />
}

function GlobalEntriesTabContent() {
    const { sk } = useAuth()
    const { settings } = useAdmin()
    const [events, setEvents] = useState<Event[]>([])
    const [loading, setLoading] = useState(true)
    const pool = getNostrPool()

    useEffect(() => {
        console.log('[GlobalEntries] useEffect triggered')

        setLoading(true)
        setEvents([])

        // Use the same reliable relays as the home feed for consistency
        const defaultLocalRelays = [
            'wss://relay.damus.io',
            'wss://nos.lol',
            'wss://relay.nostr.band',
        ]

        const customRelays = Array.isArray(settings.customRelays) ? settings.customRelays : []
        const relaysToUse = customRelays.length > 0 ? customRelays : defaultLocalRelays

        console.log('[GlobalEntries] Final relays to use:', relaysToUse)

        const filter = {
            kinds: [1],
            limit: 100,
            '#t': ['mavililer'] // Single tag for maximum compatibility
        }

        try {
            // PASS THE FILTER OBJECT DIRECTLY (NO ARRAY) - Matches useLocalFeed
            const sub = pool.subscribeMany(relaysToUse, filter, {
                onevent(event) {
                    setEvents(prev => {
                        if (prev.find(e => e.id === event.id)) return prev
                        return [...prev, event]
                            .sort((a, b) => b.created_at - a.created_at)
                            .slice(0, 100)
                    })
                },
                oneose() {
                    console.log('[GlobalEntries] EOSE received')
                    setLoading(false)
                }
            })

            return () => sub.close()
        } catch (error) {
            console.error('[GlobalEntries] Error:', error)
            setLoading(false)
        }
    }, [settings.customRelays, pool])

    const handleStrongDelete = async (eventId: string, topicTitle?: string) => {
        console.log('[StrongDelete] Starting for event:', eventId)
        console.log('[StrongDelete] SK available:', !!sk)
        console.log('[StrongDelete] Topic:', topicTitle)

        if (!window.confirm('GÜÇLÜ SİL: Bu içerik tüm kullanıcılardan gizlenecek (Global Liste). Onaylıyor musunuz?')) {
            console.log('[StrongDelete] User cancelled')
            return
        }

        try {
            // Step 1: Kind 5 deletion event
            console.log('[StrongDelete] Step 1: Publishing Kind 5 event')
            try {
                const { deleteEvent } = await import('../lib/nostr')
                const deleteResult = await deleteEvent(eventId, 'Admin delete', sk, topicTitle)
                console.log('[StrongDelete] Kind 5 published:', deleteResult)
            } catch (e) {
                console.error('[StrongDelete] Kind 5 error:', e)
            }

            // Step 2: Add to global moderation list
            console.log('[StrongDelete] Step 2: Adding to global moderation list')
            const { addToList } = await import('../services/listService')
            const result = await addToList('moderation', { type: 'event', value: eventId }, sk, 'moderation_log')
            console.log('[StrongDelete] addToList result:', result)

            if (!result.success) {
                console.error('[StrongDelete] Failed to add to list:', result.error)
                alert('Listeye eklenirken hata: ' + result.error)
            } else {
                console.log('[StrongDelete] Successfully added to moderation list')
                alert('İşlem tamamlandı. Sayfayı yenilediğinizde içerik kaybolmalı.')
            }
        } catch (e: any) {
            console.error('[StrongDelete] Unexpected error:', e)
            alert('Hata oluştu: ' + e.message)
        }
    }

    return (
        <div className="space-y-4">
            <h3 className="font-poppins font-bold text-xl text-gray-900 dark:text-white">Son Entryler (Global Akış)</h3>
            <div className="space-y-2">
                {events.map(event => {
                    const topicTag = event.tags.find(t => t[0] === 't')?.[1]
                    return (
                        <div key={event.id} className="p-4 bg-white dark:bg-surface-900 rounded-lg border border-surface-200 dark:border-surface-700 flex justify-between items-start gap-4">
                            <div className="flex-1 overflow-hidden">
                                <div className="text-xs text-mavi-600 dark:text-mavi-400 mb-1 font-bold">{topicTag ? `#${topicTag}` : 'No Topic'}</div>
                                <div className="text-sm text-gray-900 dark:text-gray-100 line-clamp-2">{event.content}</div>
                                <div className="text-xs text-gray-400 mt-1 font-mono">ID: {event.id.slice(0, 8)} | Author: {event.pubkey.slice(0, 6)}...</div>
                            </div>
                            <button
                                onClick={() => handleStrongDelete(event.id, topicTag)}
                                className="p-2 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors"
                                title="Güçlü Sil"
                            >
                                <ShieldAlert className="w-5 h-5" />
                            </button>
                        </div>
                    )
                })}
                {events.length === 0 && !loading && <div className="text-center text-gray-500">Entry bulunamadı.</div>}
                {loading && <div className="text-center text-gray-500">Yükleniyor...</div>}
            </div>
        </div>
    )
}

function ModerationLogTab() {
    const { blockedEventIds } = useGlobalModeration()

    return (
        <div className="space-y-6">
            <header>
                <h2 className="font-poppins font-bold text-2xl text-gray-900 dark:text-white">Moderasyon Kaydı</h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Global olarak gizlenen içeriklerin listesi (Kind 30000).</p>
            </header>

            <div className="bg-white dark:bg-surface-900 rounded-xl border border-surface-200 dark:border-surface-700 p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
                    <ShieldCheck className="w-5 h-5 text-green-500" />
                    Aktif Gizlenen İçerikler ({blockedEventIds.size})
                </h3>

                <div className="max-h-[500px] overflow-y-auto space-y-2 font-mono text-xs">
                    {Array.from(blockedEventIds).map(id => (
                        <div key={id} className="p-2 bg-gray-50 dark:bg-surface-950 rounded flex items-center justify-between border border-gray-100 dark:border-surface-800">
                            <span className="text-gray-600 dark:text-gray-400">{id}</span>
                            <span className="text-red-500 font-bold px-2 text-xs border border-red-200 bg-red-50 rounded">BLOCKED</span>
                        </div>
                    ))}
                    {blockedEventIds.size === 0 && <div className="text-gray-400 italic text-center py-4">Henüz gizlenen içerik yok.</div>}
                </div>
            </div>
        </div>
    )
}

function FeaturesTab() {
    const { settings, toggleFeature, publishSettings, isPublishing, lastSyncedAt } = useAdmin()

    const features = [
        { id: 'chatEnabled', label: 'Sohbet Odaları', description: 'Kullanıcılar arası gerçek zamanlı mesajlaşma.', icon: MessageSquare },
        { id: 'flowEnabled', label: 'Akış (Flow)', icon: Activity, description: 'Global ve yerel Nostr akışı.' },
        { id: 'exploreEnabled', label: 'Keşfet', icon: Eye, description: 'Yeni içerikleri keşfetme sayfası.' },
        { id: 'searchEnabled', label: 'Arama', icon: Check, description: 'İçerik ve kullanıcı arama motoru.' },
        { id: 'profileEditing', label: 'Profil Düzenleme', icon: Users, description: 'Kullanıcıların profillerini güncelleme yetkisi.' },
        { id: 'bookmarksEnabled', label: 'Yer İmleri', icon: ShieldCheck, description: 'İçerikleri kaydetme özelliği.' },
        { id: 'zapsEnabled', label: 'Bitcoin (Zap)', icon: Zap, description: 'Bitcoin Lightning ile destek olma.' },
        { id: 'eksiAgenda', label: 'Ekşi Gündem', icon: Bot, description: 'Ana sayfadaki Ekşi Sözlük gündem modülü.' },
        { id: 'externalResources', label: 'Dış Kaynaklar', icon: Link2, description: 'Başlık altındaki Ekşi ve Facebook linkleri.' },
    ]

    return (
        <div className="space-y-8">
            <header>
                <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-5 h-5 text-mavi-500" />
                    <h2 className="font-poppins font-bold text-2xl text-gray-900 dark:text-white">Özellik Kontrolü</h2>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Site özelliklerini anlık olarak açıp kapatabilirsiniz.</p>
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {features.map((feature) => {
                    const isEnabled = (settings.features as any)[feature.id]
                    return (
                        <div
                            key={feature.id}
                            className={`p-4 rounded-2xl border transition-all flex items-center justify-between ${isEnabled
                                ? 'bg-mavi-50/50 dark:bg-mavi-900/10 border-mavi-100 dark:border-mavi-900/30'
                                : 'bg-surface-50 dark:bg-surface-800/50 border-surface-200 dark:border-surface-700 opacity-70'
                                }`}
                        >
                            <div className="flex items-center gap-2 lg:gap-4 min-w-0">
                                <div className={`p-3 rounded-xl flex-shrink-0 ${isEnabled ? 'bg-white dark:bg-surface-900 text-mavi-500' : 'bg-white dark:bg-surface-900 text-gray-400'}`}>
                                    <feature.icon className="w-5 h-5" />
                                </div>
                                <div className="min-w-0">
                                    <h4 className="font-poppins font-bold text-gray-900 dark:text-white text-sm truncate">{feature.label}</h4>
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate lg:whitespace-normal">{feature.description}</p>
                                </div>
                            </div>

                            <button
                                onClick={() => toggleFeature(feature.id as any)}
                                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${isEnabled ? 'bg-mavi-500' : 'bg-gray-300 dark:bg-surface-700'
                                    }`}
                            >
                                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${isEnabled ? 'translate-x-6' : 'translate-x-0'
                                    }`} />
                            </button>
                        </div>
                    )
                })}
            </div>

            <div className="p-4 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-900/30 rounded-xl flex items-center justify-between gap-4">
                <p className="text-xs text-orange-800 dark:text-orange-500 flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4" />
                    <span><strong>Admin Notu:</strong> Özellikler kapatıldığında kullanıcılar göremez ancak adminler her zaman tam erişime sahiptir.</span>
                </p>
                <button
                    onClick={publishSettings}
                    disabled={isPublishing}
                    className="flex items-center gap-2 px-6 py-2 bg-mavi-500 hover:bg-mavi-600 disabled:bg-gray-400 text-white rounded-xl font-poppins font-bold text-sm transition-all shadow-lg shadow-mavi-500/20"
                >
                    {isPublishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    {isPublishing ? 'Yayınlanıyor...' : 'Değişiklikleri Yayınla'}
                </button>
            </div>

            {lastSyncedAt && (
                <p className="text-[10px] text-gray-500 dark:text-gray-400 text-right italic">
                    Son senkronizasyon: {new Date(lastSyncedAt * 1000).toLocaleString()}
                </p>
            )}
        </div>
    )
}

function SystemTab() {
    const { settings, updateSettings, publishSettings, isPublishing, lastSyncedAt } = useAdmin()

    return (
        <div className="space-y-8">
            <header>
                <div className="flex items-center gap-2 mb-1">
                    <Settings className="w-5 h-5 text-purple-500" />
                    <h2 className="font-poppins font-bold text-2xl text-gray-900 dark:text-white">Sistem Ayarları</h2>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm">Uygulamanın çalışma modlarını ve genel ayarlarını yönetin.</p>
            </header>

            <div className="space-y-6">
                <div className="bg-white dark:bg-surface-900 rounded-2xl p-6 border border-surface-200 dark:border-surface-700">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4 min-w-0">
                            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 text-purple-600 rounded-xl flex-shrink-0">
                                <ShieldCheck className="w-6 h-6" />
                            </div>
                            <div className="min-w-0">
                                <h4 className="font-poppins font-bold text-gray-900 dark:text-white">Namespace İzolasyonu</h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Sadece #mavililer etiketi olan içerikleri gösterir.</p>
                            </div>
                        </div>
                        <button
                            onClick={() => updateSettings({ isolateCommunity: !settings.isolateCommunity })}
                            className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${settings.isolateCommunity ? 'bg-purple-500' : 'bg-gray-300 dark:bg-surface-700'}`}
                        >
                            <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${settings.isolateCommunity ? 'translate-x-6' : 'translate-x-0'}`} />
                        </button>
                    </div>

                    <div className="p-4 bg-surface-50 dark:bg-surface-800 rounded-xl text-xs text-gray-500 leading-relaxed font-merriweather italic">
                        Bu ayar açık olduğunda uygulama "Mavililer Sözlük" özelinde çalışır. Kapatıldığında tüm Nostr ağına (Kind 1) açık bir istemci gibi davranır.
                    </div>
                </div>

                <div className="bg-white dark:bg-surface-900 rounded-2xl p-6 border border-surface-200 dark:border-surface-700 opacity-50 cursor-not-allowed">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-gray-100 dark:bg-surface-800 text-gray-600 rounded-xl">
                                <Activity className="w-6 h-6" />
                            </div>
                            <div>
                                <h4 className="font-poppins font-bold text-gray-900 dark:text-white">Bakım Modu (Maintenance)</h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Yakında eklenecek.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-900/20 rounded-2xl">
                <div>
                    <h4 className="text-sm font-poppins font-bold text-purple-900 dark:text-purple-400">Değişiklikleri Kaydet</h4>
                    <p className="text-[11px] text-purple-700 dark:text-purple-500">Yaptığınız sistem ayarlarını tüm ağa duyurun.</p>
                </div>
                <button
                    onClick={publishSettings}
                    disabled={isPublishing}
                    className="flex items-center gap-2 px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-xl font-poppins font-bold text-sm transition-all shadow-lg shadow-purple-500/20"
                >
                    {isPublishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    {isPublishing ? 'Güncelleniyor...' : 'Ağı Güncelle'}
                </button>
            </div>

            {lastSyncedAt && (
                <p className="text-[10px] text-gray-500 dark:text-gray-400 text-right italic">
                    Son senkronizasyon: {new Date(lastSyncedAt * 1000).toLocaleString()}
                </p>
            )}
        </div>
    )
}

function BotsTab({ setActiveTab }: { setActiveTab: (tab: Tab) => void }) {
    const { settings, updateSettings } = useAdmin()
    const [botHealth, setBotHealth] = useState<'loading' | 'online' | 'offline'>('loading')
    const [isActing, setIsActing] = useState<string | null>(null)
    const [newBotPk, setNewBotPk] = useState('')

    useEffect(() => {
        checkHealth()
    }, [])

    const checkHealth = async () => {
        setBotHealth('loading')
        const isOnline = await botService.checkBotHealth()
        setBotHealth(isOnline ? 'online' : 'offline')
    }

    const runAction = async (action: string, fn: () => Promise<any>) => {
        setIsActing(action)
        try {
            const result = await fn()
            if (result.success) {
                alert(`${action} başarıyla tamamlandı.`)
            } else {
                alert(`${action} başarısız oldu.`)
            }
        } catch (e: any) {
            alert(`Hata: ${e.message}`)
        } finally {
            setIsActing(null)
        }
    }

    const handleAddBot = () => {
        if (newBotPk.length < 10) return
        const currentBots = settings.authorizedBots || []
        if (currentBots.includes(newBotPk)) return
        updateSettings({ authorizedBots: [...currentBots, newBotPk] })
        setNewBotPk('')
    }

    const removeBot = (pk: string) => {
        updateSettings({ authorizedBots: settings.authorizedBots.filter(p => p !== pk) })
    }

    return (
        <div className="space-y-8">
            <header className="flex items-center justify-between">
                <div>
                    <h2 className="font-poppins font-bold text-2xl text-gray-900 dark:text-white">Bot Yönetimi</h2>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Resmi bot hesapları ve servis kontrolleri.</p>
                </div>
                <div className={`p-2 rounded-xl flex items-center gap-2 text-xs font-bold font-poppins transition-all ${botHealth === 'online' ? 'bg-green-500/10 text-green-600' :
                    botHealth === 'offline' ? 'bg-red-500/10 text-red-600' : 'bg-gray-100 text-gray-400'
                    }`}>
                    <Activity className={`w-4 h-4 ${botHealth === 'loading' ? 'animate-spin' : ''}`} />
                    {botHealth === 'online' ? 'SERVİS AKTİF' : botHealth === 'offline' ? 'SERVİS ÇEVRİMDIŞI' : 'KONTROL EDİLİYOR...'}
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Bot Actions */}
                <div className="space-y-4">
                    <h3 className="text-sm font-poppins font-bold text-gray-400 uppercase tracking-widest pl-1">Hızlı İşlemler</h3>
                    <div className="grid grid-cols-1 gap-3">
                        <ActionButton
                            label="Bot Profilini Güncelle"
                            desc="Botun Nostr profil metalarını (Kind 0) yeniler."
                            icon={Users}
                            loading={isActing === 'profile'}
                            onClick={() => runAction('profile', botService.updateBotProfile)}
                        />
                        <ActionButton
                            label="Daily Digest Yayınla"
                            desc="Günün en popüler 5 başlığını bot hesabı ile duyurur."
                            icon={FileText}
                            loading={isActing === 'digest'}
                            onClick={() => runAction('digest', () => botService.publishDailyDigest(['gündem', 'nostr', 'mavililer']))}
                        />
                        <ActionButton
                            label="Genel Duyuru Yap"
                            desc="Önemli sistem duyurularını bot üzerinden yayınlar."
                            icon={ShieldAlert}
                            loading={isActing === 'announce'}
                            onClick={() => runAction('announce', () => botService.announceNewTopic('Mavililer Sözlük v2.0 Yayında!'))}
                        />
                    </div>
                </div>

                {/* Authorized Bots List */}
                <div className="space-y-4">
                    <h3 className="text-sm font-poppins font-bold text-gray-400 uppercase tracking-widest pl-1">Yetkili Bot Listesi</h3>
                    <div className="bg-white dark:bg-surface-900 rounded-2xl border border-surface-200 dark:border-surface-700 p-6 space-y-4">
                        <div className="flex gap-2">
                            <input
                                value={newBotPk}
                                onChange={(e) => setNewBotPk(e.target.value)}
                                placeholder="Bot Pubkey (hex)"
                                className="flex-1 bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 rounded-xl px-4 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-mavi-500"
                            />
                            <button
                                onClick={handleAddBot}
                                className="p-2 bg-mavi-500 text-white rounded-xl hover:bg-mavi-600 transition-colors"
                            >
                                <Plus className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-2">
                            {(settings.authorizedBots || []).length === 0 ? (
                                <p className="text-xs text-gray-400 italic text-center py-4">Henüz kayıtlı bot yok.</p>
                            ) : (
                                settings.authorizedBots.map(pk => (
                                    <div key={pk} className="flex items-center justify-between p-3 bg-surface-50 dark:bg-surface-800 rounded-xl border border-surface-100 dark:border-surface-700">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="p-2 bg-mavi-100 dark:bg-mavi-900/30 text-mavi-600 rounded-lg">
                                                <Bot className="w-4 h-4" />
                                            </div>
                                            <span className="text-[10px] font-mono text-gray-600 dark:text-gray-400 truncate">{pk}</span>
                                        </div>
                                        <button
                                            onClick={() => removeBot(pk)}
                                            className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="p-4 bg-mavi-50 dark:bg-mavi-900/10 border border-mavi-100 dark:border-mavi-900/20 rounded-xl">
                        <p className="text-[11px] text-mavi-700 dark:text-mavi-400 leading-relaxed italic">
                            <strong>İpucu:</strong> Buraya eklediğiniz botlar, sistem tarafından "Resmi Hizmet" olarak tanınır.
                            Botların sizin adınıza işlem yapabilmesi için <span className="font-bold underline cursor-pointer" onClick={() => setActiveTab('delegation')}>Delegasyon</span> sekmesinden token oluşturmanız gerekebilir.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    )
}

function ActionButton({ label, desc, icon: Icon, onClick, loading }: any) {
    return (
        <button
            onClick={onClick}
            disabled={loading}
            className="w-full p-4 bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-700 rounded-2xl flex items-center gap-4 text-left hover:border-mavi-500/50 hover:bg-mavi-50/10 transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
        >
            <div className="p-3 bg-surface-50 dark:bg-surface-800 text-gray-400 group-hover:text-mavi-500 group-hover:bg-mavi-500/10 rounded-xl transition-all">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Icon className="w-5 h-5" />}
            </div>
            <div>
                <h4 className="font-poppins font-bold text-gray-900 dark:text-white text-sm">{label}</h4>
                <p className="text-[10px] text-gray-500 dark:text-gray-400">{desc}</p>
            </div>
        </button>
    )
}
